import { useId, useState, type ReactNode, type KeyboardEvent } from 'react';
import type { CampaignCreative } from '../campaignsApi';
import type { AdChannel } from '../campaignChannels';
import { creativeReviewLevel, metaReviewLevels, type MetaReviewLevel } from '../metaReviewLevels';
import './campaign-review-tabs.css';

interface Props {
  channels: AdChannel[];
  creatives: CampaignCreative[];
  level: MetaReviewLevel;
  onLevelChange: (level: MetaReviewLevel) => void;
  googleReview: ReactNode;
  children: ReactNode;
}

interface Tab { value: string; label: string; title?: string; disabled?: boolean }

function ReviewTabBar({ id, label, tabs, selected, onSelect }: {
  id: string; label: string; tabs: Tab[]; selected: string; onSelect: (value: string) => void;
}) {
  function navigate(event: KeyboardEvent<HTMLButtonElement>, value: string) {
    const available = tabs.filter(tab => !tab.disabled);
    const index = available.findIndex(tab => tab.value === value);
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!offset && !['Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? available[0] : event.key === 'End' ? available[available.length - 1]
      : available[(index + offset + available.length) % available.length];
    if (!next) return;
    onSelect(next.value);
    event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)?.focus();
  }
  return <div className="campaign-review-tabbar" role="tablist" aria-label={label}>
    {tabs.map(tab => <button key={tab.value} type="button" role="tab" id={`${id}-tab-${tab.value}`}
      aria-controls={`${id}-panel-${tab.value}`} aria-selected={selected === tab.value}
      tabIndex={selected === tab.value ? 0 : -1} disabled={tab.disabled} title={tab.title}
      data-value={tab.value} onClick={() => onSelect(tab.value)} onKeyDown={event => navigate(event, tab.value)}>
      {tab.label}
    </button>)}
  </div>;
}

export function CampaignReviewTabs({ channels, creatives, level, onLevelChange, googleReview, children }: Props) {
  const id = useId();
  const [selected, setSelected] = useState<AdChannel>(channels[0] || 'meta');
  const channel = channels.includes(selected) ? selected : channels[0];
  const currentLevel = metaReviewLevels.find(item => item.value === level)!;
  const tabs = (['google', 'meta'] as const).map(value => ({ value,
    label: value === 'google' ? 'Google Ads' : 'Meta Ads', disabled: !channels.includes(value),
    title: channels.includes(value) ? undefined : 'Selecione este canal no briefing para revisar seus anúncios.',
  }));
  const levels = metaReviewLevels.map((item, index) => ({ value: item.value,
    label: `${index + 1}. ${item.label} (${creatives.filter(creative => creativeReviewLevel(creative) === item.value).length})`,
    title: item.description,
  }));
  return <section className="campaign-review-tabs">
    <ReviewTabBar id={id} label="Canal dos anúncios" tabs={tabs} selected={channel}
      onSelect={value => setSelected(value as AdChannel)}/>
    <div role="tabpanel" id={`${id}-panel-google`} aria-labelledby={`${id}-tab-google`} hidden={channel !== 'google'}>
      {channels.includes('google') && googleReview}
    </div>
    <div role="tabpanel" id={`${id}-panel-meta`} aria-labelledby={`${id}-tab-meta`} hidden={channel !== 'meta'}>
      {channels.includes('meta') && <>
        <ReviewTabBar id={`${id}-level`} label="Nível de consciência Meta" tabs={levels} selected={level}
          onSelect={value => onLevelChange(value as MetaReviewLevel)}/>
        {metaReviewLevels.map(item => <div key={item.value} role="tabpanel" id={`${id}-level-panel-${item.value}`}
          aria-labelledby={`${id}-level-tab-${item.value}`} hidden={level !== item.value}>
          {level === item.value && <><p className="campaign-review-level-description">{currentLevel.description}</p>{children}</>}
        </div>)}
      </>}
    </div>
  </section>;
}
